import * as React from "react";
import {
  Box,
  Drawer,
  IconButton,
  Typography,
  Divider,
  styled,
} from "@mui/material";
import { ChevronLeft, ChevronRight } from "@mui/icons-material";
import { CustomNavigationList } from "./CustomNavigationList";
import { useContext } from "react";
import osiLogo from "../utils/images/OSI-LOGO.png";
import AuthContext from "../context/AuthContext";

const drawerWidth = 250;
const collapsedWidth = 64;

const StyledDrawer = styled(Drawer)(({ theme, open }) => ({
  width: open ? drawerWidth : collapsedWidth,
  flexShrink: 0,
  whiteSpace: "nowrap",
  boxSizing: "border-box",
  "& .MuiDrawer-paper": {
    width: open ? drawerWidth : collapsedWidth,
    background:
      "linear-gradient(180deg, rgb(140, 150, 169) 0%, rgba(140, 150, 169, 0.95) 100%)",
    backdropFilter: "blur(20px)",
    borderRight: "1px solid rgba(255, 255, 255, 0.1)",
    boxShadow: "4px 0 20px rgba(140, 150, 169, 0.3)",
    color: "white",
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
    overflowX: "hidden",
  },
}));

const DrawerHeader = styled("div")<{ isopen: boolean }>(
  ({ theme, isopen }) => ({
    display: "flex",
    flexDirection: isopen ? "row" : "column",
    alignItems: "center",
    justifyContent: isopen ? "space-between" : "center",
    padding: theme.spacing(2),
    background: "rgba(255, 255, 255, 0.05)",
    backdropFilter: "blur(10px)",
    borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
    minHeight: isopen ? "64px" : "auto",
    boxSizing: "border-box",
    gap: isopen ? 0 : theme.spacing(1),
  }),
);

const ButtonContainer = styled("div")<{ isopen: boolean }>(
  ({ theme, isopen }) => ({
    background: isopen ? "transparent" : "white",
    borderRadius: isopen ? "0" : "12px",
    padding: isopen ? "0" : theme.spacing(1),
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: isopen ? "auto" : "48px",
    height: isopen ? "auto" : "48px",
    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  }),
);

const LogoImage = styled("img")<{ isopen: boolean }>(({ isopen }) => ({
  width: isopen ? "60%" : "32px",
  height: "auto",
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  filter: "brightness(1.2)",
  maxWidth: isopen ? "140px" : "32px",
}));

const ToggleButton = styled(IconButton)<{ isopen: boolean }>(
  ({ theme, isopen }) => ({
    background: isopen
      ? "rgba(255, 255, 255, 0.1)"
      : "rgba(140, 150, 169, 0.8)",
    color: isopen ? "white" : "white",
    width: "40px",
    height: "40px",
    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    "&:hover": {
      background: "rgba(251, 171, 53, 0.2)",
      transform: "scale(1.1)",
    },
  }),
);

const CopyrightFooter = styled(Box)(({ theme }) => ({
  position: "absolute",
  bottom: "16px",
  left: "16px",
  color: "rgba(255, 255, 255, 0.7)",
  fontSize: "0.75rem",
  background: "rgba(0, 0, 0, 0.2)",
  padding: "4px 8px",
  borderRadius: "8px",
  backdropFilter: "blur(10px)",
}));

export function DrawerNav() {
  const { isDrawerOpen, onMoveDrawer, desktop } = useContext(AuthContext);

  return (
    <Box sx={{ display: "flex" }}>
      <StyledDrawer variant="permanent" open={isDrawerOpen}>
        <DrawerHeader isopen={isDrawerOpen}>
          <LogoImage src={osiLogo} alt="OSI logo" isopen={isDrawerOpen} />
          <ButtonContainer isopen={isDrawerOpen}>
            <ToggleButton
              onClick={() => onMoveDrawer(!isDrawerOpen)}
              isopen={isDrawerOpen}
            >
              {isDrawerOpen ? <ChevronLeft /> : <ChevronRight />}
            </ToggleButton>
          </ButtonContainer>
        </DrawerHeader>

        <Divider
          sx={{
            borderColor: "rgba(255, 255, 255, 0.2)",
            margin: 0,
          }}
        />

        <Box
          sx={{
            flex: 1,
            overflow: "auto",
            padding: "8px 0",
          }}
        >
          <CustomNavigationList
            isDrawerOpen={isDrawerOpen}
            onCloseMenu={
              !desktop
                ? () => {
                    onMoveDrawer(false);
                  }
                : () => {}
            }
          />
        </Box>

        {isDrawerOpen && (
          <CopyrightFooter>
            <Typography variant="caption">Copyright © 2025 OSI</Typography>
          </CopyrightFooter>
        )}
      </StyledDrawer>
    </Box>
  );
}
